// The maths behind drawings. Pure functions of numbers -- no chart, no DOM --
// so the part that decides "did the finger hit this line" can be reasoned
// about and tested without a browser.
//
// Everything here works in PIXEL space. Anchors are stored as (time, price)
// because those survive a timeframe switch and a history prepend, but hit
// testing and dragging happen where the finger is.

// Distance from a point to a line SEGMENT, clamped at both ends.
//
// The infinite-line formula is the classic bug here: it reports a hit far past
// both endpoints, so a trader taps empty space and drags a line that is not
// under their finger. The clamp is the whole difference.
export function distanceToSegment(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lengthSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

// A horizontal line spans the whole pane, so only the vertical distance counts.
export function distanceToHorizontal(py, y) {
  return Math.abs(py - y);
}

// Interpolate a trendline's y at an arbitrary x, in PIXEL space.
//
// This is equivalent to interpolating in price space only while the price
// scale is linear. If a log scale is ever offered, a line drawn between two
// prices would silently bend -- which is the correct behaviour for a
// pixel-drawn line and the wrong one for a trader's expectation. Whoever adds
// the log toggle has to decide that deliberately, so it is stated here.
export function interpolateY(x, x1, y1, x2, y2) {
  if (x2 === x1) return y1;
  return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
}

// Touch needs a far bigger target than a mouse. The handles render at r≈6 but
// must be grabbable at 22 -- this is a phone-first product and 44 CSS px is
// the floor for a touch target.
export const TOLERANCE = {
  mouse: { line: 8, handle: 10 },
  touch: { line: 16, handle: 22 },
};

export function toleranceFor(pointerType) {
  return pointerType === "mouse" ? TOLERANCE.mouse : TOLERANCE.touch;
}

// Which part of a drawing the finger landed on. Precedence is top-down --
// handles beat the segment, the segment beats the interior -- because a finger
// near an endpoint almost always means "move this end", and resolving that as
// "move the whole line" is the single most infuriating drawing bug.
export function hitParts(kind, anchorsPx, x, y, tol, options = {}) {
  // NEAREST handle, not the first one in range. With a short line both
  // endpoints sit inside a 22px touch target, and returning the first meant
  // grabbing one end and dragging the other.
  let best = null;
  for (let i = 0; i < anchorsPx.length; i += 1) {
    const a = anchorsPx[i];
    if (!a) continue;
    const d = Math.hypot(x - a.x, y - a.y);
    if (d <= tol.handle && (!best || d < best.d)) best = { d, index: i };
  }
  if (best) return { part: "handle", index: best.index };

  if (kind === "hline") {
    const a = anchorsPx[0];
    if (a && distanceToHorizontal(y, a.y) <= tol.line) return { part: "body", index: null };
    return null;
  }

  if (kind === "vline") {
    // Grabbed anywhere along its full height, like the hline along its width.
    const a = anchorsPx[0];
    if (a && Math.abs(x - a.x) <= tol.line) return { part: "body", index: null };
    return null;
  }

  const [a, b] = anchorsPx;
  if (!a || !b) return null;

  if (kind === "rect") {
    // The four EDGES that are actually drawn, not the diagonal between the
    // anchors. Hit-testing the diagonal made a rectangle grabbable only along
    // an invisible line through its middle and nowhere on its border.
    const left = Math.min(a.x, b.x), right = Math.max(a.x, b.x);
    const top = Math.min(a.y, b.y), bottom = Math.max(a.y, b.y);
    const edges = [
      [left, top, right, top], [left, bottom, right, bottom],
      [left, top, left, bottom], [right, top, right, bottom],
    ];
    for (const [x1, y1, x2, y2] of edges) {
      if (distanceToSegment(x, y, x1, y1, x2, y2) <= tol.line) {
        return { part: "body", index: null };
      }
    }
    return null;
  }

  if (kind === "fib") {
    // A fib is GRABBED where it is PAINTED: horizontal level lines running
    // from the earlier anchor to the pane's right edge, plus the faint
    // measured-swing diagonal. Testing only the diagonal made every visible
    // level line untouchable -- the rect bug all over again.
    const startX = Math.min(a.x, b.x);
    if (x >= startX - tol.line) {
      for (const ratio of [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1]) {
        const yr = a.y + (b.y - a.y) * ratio;
        if (Math.abs(y - yr) <= tol.line) return { part: "body", index: null };
      }
    }
    if (distanceToSegment(x, y, a.x, a.y, b.x, b.y) <= tol.line) {
      return { part: "body", index: null };
    }
    return null;
  }

  if (kind === "trade") {
    // Entry/SL/TP render as horizontal lines from the entry anchor's x to the
    // pane edge. Each line belongs to ITS anchor -- a finger on the SL line
    // means "move the stop", not "move the trade" -- so return the anchor's
    // index and let the drag logic treat it like a handle grab.
    const startX = a.x;
    if (x >= startX - tol.line) {
      let nearest = null;
      for (let i = 0; i < anchorsPx.length; i += 1) {
        const pt = anchorsPx[i];
        if (!pt) continue;
        const d = Math.abs(y - pt.y);
        if (d <= tol.line && (!nearest || d < nearest.d)) nearest = { d, index: i };
      }
      if (nearest) return { part: "handle", index: nearest.index };
    }
    return null;
  }

  if (kind === "channel") {
    // Both rails are grabbable; the offset rail's position derives from the
    // third anchor exactly as the renderer derives it.
    const c = anchorsPx[2];
    if (distanceToSegment(x, y, a.x, a.y, b.x, b.y) <= tol.line) {
      return { part: "body", index: null };
    }
    if (c) {
      const slope = (b.x - a.x) === 0 ? 0 : (b.y - a.y) / (b.x - a.x);
      const dy = c.y - (a.y + slope * (c.x - a.x));
      if (distanceToSegment(x, y, a.x, a.y + dy, b.x, b.y + dy) <= tol.line) {
        return { part: "handle", index: 2 };
      }
    }
    return null;
  }

  if (kind === "ray") {
    // A ray is drawn to the pane edge, and its extension is the whole point of
    // it being a ray. Testing only the segment between the two anchors meant
    // the visible part could not be selected, dragged or long-pressed.
    const width = options.paneWidth;
    let end = b;
    if (width) {
      const endX = b.x >= a.x ? width : 0;
      end = { x: endX, y: interpolateY(endX, a.x, a.y, b.x, b.y) };
    }
    if (distanceToSegment(x, y, a.x, a.y, end.x, end.y) <= tol.line) {
      return { part: "body", index: null };
    }
    return null;
  }

  if (distanceToSegment(x, y, a.x, a.y, b.x, b.y) <= tol.line) {
    return { part: "body", index: null };
  }
  return null;
}

// Snap an anchor to something the chart already knows about.
//
// A snapped anchor is PROVABLY on an engine level rather than near one, which
// turns "your line is around D-R1" into a fact the product can state. That is
// why the snap targets carry a label: what it locked onto travels with it.
export function snapPrice(price, y, candidates, priceToY, tolerancePx) {
  let best = null;
  for (const c of candidates) {
    if (c == null || c.price == null) continue;
    const cy = priceToY(c.price);
    if (cy === null || cy === undefined) continue;
    const distance = Math.abs(cy - y);
    if (distance <= tolerancePx && (!best || distance < best.distance)) {
      best = { price: c.price, label: c.label || null, distance };
    }
  }
  return best ? { price: best.price, snappedTo: best.label } : { price, snappedTo: null };
}

// Fibonacci retracement levels between two prices. p1 is the 0 end.
export const FIB_RATIOS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];

export function fibLevels(p1, p2) {
  return FIB_RATIOS.map((r) => ({ ratio: r, price: p2 + (p1 - p2) * r }));
}

// What a two-anchor drawing measures. The ATR and R columns are the part no
// phone charting app gives: "this line is 0.31 ATR away" and "your stop is
// 0.4R" are the readings that decide whether a drawing is a trade or a doodle.
export function measure(a, b, { atr = null, bars = null } = {}) {
  if (!a || !b) return null;
  const delta = b.p - a.p;
  const out = {
    from: a.p,
    to: b.p,
    delta: Math.round(delta * 100) / 100,
    pct: a.p ? Math.round((delta / a.p) * 10000) / 100 : null,
    bars: bars == null ? null : bars,
    atr_multiple: atr ? Math.round((Math.abs(delta) / atr) * 100) / 100 : null,
  };
  return out;
}
